import React, { useState, useEffect, useMemo } from 'react';
import Lottie from 'lottie-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useDeviceDetection } from '@src/shared/hooks';
import { BackgroundLayer } from './components/background-layer';

import loadingDots from '@src/assets/lotties/loading_dots.json';
import frameSvg from '@src/assets/images/frame.svg';
import idwall1 from '@src/assets/images/idwall1.svg';
import idwall2 from '@src/assets/images/idwall2.svg';
import idwall3 from '@src/assets/images/idwall3.svg';
import idwall4 from '@src/assets/images/idwall4.svg';

interface LoadingProps {
  isVisible?: boolean;
  currentImageIndex?: number; // Controle direto da imagem (0-3)
  showTransitionImage?: boolean; // Controle específico da 4ª imagem
  onTransitionComplete?: () => void; // Callback quando transição termina
}

const images = [idwall1, idwall2, idwall3, idwall4];
const texts = [
  'Seja bem-vindo! Estamos consultando seus dados para iniciar a simulação.',
  'Analisando seu perfil para encontrar as melhores condições para você.',
  'Finalizando os últimos detalhes para apresentar suas opções.',
  'Boa notícia! Preparamos ofertas exclusivas feitas sob medida para você.',
];

export const Loading: React.FC<LoadingProps> = ({
  isVisible = true,
  currentImageIndex = 0,
  showTransitionImage = false,
  onTransitionComplete,
}) => {
  const deviceType = useDeviceDetection();
  const [displayImageIndex, setDisplayImageIndex] = useState(0);
  const [shouldExit, setShouldExit] = useState(false);

  useEffect(() => {
    if (showTransitionImage) {
      setDisplayImageIndex(3);

      const exitTimer = setTimeout(() => {
        setShouldExit(true);
      }, 4000);

      return () => clearTimeout(exitTimer);
    } else {
      setDisplayImageIndex(currentImageIndex);
      setShouldExit(false);
    }
  }, [currentImageIndex, showTransitionImage]);

  const handleExitAnimationComplete = () => {
    onTransitionComplete?.();
  };

  const WordAnimation: React.FC<{
    text: string;
    index: number;
  }> = ({ text, index }) => {
    const words = text.split(' ');

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, scale: 0.9, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{
          opacity: 0,
          scale: 0.95,
          y: -5,
          transition: { duration: 0.5, delay: 0, ease: 'easeOut' },
        }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className='text-center'
      >
        {words.map((word, wordIndex) => (
          <motion.span
            key={`${word}-${wordIndex}-${index}`}
            initial={{ opacity: 0, y: 15, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{
              opacity: 0,
              y: -5,
              scale: 0.8,
              transition: { duration: 0.5, delay: 0, ease: 'easeOut' },
            }}
            transition={{
              duration: 0.5,
              delay:
                wordIndex * 0.1 + (index === 0 ? 0.5 : index === 3 ? 0.1 : 0.3),
              ease: 'easeOut',
            }}
            className='inline-block mr-2 font-semibold text-white tracking-normal'
          >
            {word}
          </motion.span>
        ))}
      </motion.div>
    );
  };

  const deviceStyles = useMemo(() => {
    if (deviceType === 'mobile') {
      return {
        container: {
          paddingRight: '24px',
          paddingLeft: '24px',
        },
        images: {
          width: '240px',
          height: '240px',
        },
        frame: {
          width: '274px',
          height: '272px',
          marginBottom: '48px',
        },
        text: {
          lineHeight: '140%',
          fontSize: '20px',
          marginBottom: '48px',
          height: '88px',
        },
        loadingDots: {
          width: '52px',
          height: '20px',
        },
      };
    }

    return {
      container: {
        paddingRight: '16px',
        paddingLeft: '16px',
      },
      images: {
        width: '312px',
        height: '312px',
      },
      frame: {
        width: '358px',
        height: '354px',
        marginBottom: '56px',
      },
      text: {
        lineHeight: '140%',
        fontSize: '32px',
        marginBottom: '64px',
        height: '88px',
      },
      loadingDots: {
        height: '24px',
        width: '64px',
      },
    };
  }, [deviceType]);

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence onExitComplete={handleExitAnimationComplete}>
      {!shouldExit && (
        <motion.div
          key='loading-container'
          className='fixed inset-0 z-[9999] flex items-center justify-center'
          style={{ backgroundColor: '#001833' }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: {
              duration: 0.3,
              delay: 2.2, // Após todas as animações (Frame: 0.5s delay + 1.5s duration = 2.0s + 0.2s buffer)
              ease: 'easeInOut',
            },
          }}
        >
          <BackgroundLayer deviceType={deviceType} shouldExit={shouldExit} />

          <div
            className='relative z-10 flex flex-col items-center justify-center max-w-2xl mx-auto'
            style={deviceStyles.container}
          >
            <div
              className='relative flex items-center justify-center'
              style={deviceStyles.frame}
            >
              <motion.div
                key='frame-container'
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{
                  opacity: [1, 1, 0],
                  scale: [1, 1.3, 1.3],
                  y: [0, 0, 100],
                  transition: {
                    duration: 1.5, // 0.7s expansão + 0.8s movimento para baixo
                    times: [0, 0.47, 1], // 47% expansão, 53% movimento para baixo + fadeOut
                    ease: ['easeOut', 'easeIn'],
                    delay: 0.5, // Após fadeOut do texto (0.5s)
                  },
                }}
                transition={{ duration: 1.0, ease: 'easeOut', delay: 0.6 }}
                className='absolute inset-0 z-20 flex items-center justify-center'
              >
                <img src={frameSvg} alt='Frame' />
              </motion.div>

              <div
                className='relative z-10 flex items-center justify-center'
                style={deviceStyles.images}
              >
                <AnimatePresence key={'images-presence'} propagate>
                  {images.map(
                    (imageSrc, index) =>
                      displayImageIndex === index && (
                        <motion.img
                          key={`image-${index}`}
                          src={imageSrc}
                          alt={`Validation step ${index + 1}`}
                          initial={{
                            opacity: 0,
                            scale: index === 0 ? 0.4 : 1,
                            y: index === 0 ? 20 : 0,
                          }}
                          animate={{ opacity: 1, scale: 1, y: 0 }}
                          exit={{
                            opacity: 0,
                            scale: shouldExit ? 0.8 : 1,
                            y: 0,
                            transition: {
                              duration: shouldExit ? 0.8 : 0.8,
                              delay: shouldExit ? 0.7 : 0.1,
                              ease: 'easeIn',
                            },
                          }}
                          transition={{
                            duration: index === 0 ? 1.2 : 0.8,
                            ease: 'easeIn',
                            delay: index === 0 ? 0.8 : 0.1,
                          }}
                          className='absolute inset-0 max-w-full max-h-full object-contain'
                        />
                      )
                  )}
                </AnimatePresence>
              </div>
            </div>

            <div className='min-h-[60px] max-w-full' style={deviceStyles.text}>
              <WordAnimation
                key={`text-${displayImageIndex}`}
                text={texts[displayImageIndex]}
                index={displayImageIndex}
              />
            </div>

            <motion.div
              key='loading-dots-container'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{
                opacity: 0,
                transition: { duration: 0.8, delay: 0.7, ease: 'easeOut' },
              }}
              transition={{ duration: 0.8, ease: 'easeOut', delay: 1.5 }}
              style={deviceStyles.loadingDots}
            >
              <Lottie
                animationData={loadingDots}
                loop={true}
                autoplay={true}
                rendererSettings={{
                  preserveAspectRatio: 'xMidYMid meet',
                }}
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
